import {
  getPaddleFirstMonthPrice,
  getPaddlePlans,
  PADDLE_SIGNUP_DISCOUNT,
} from '@/entities/billing/lib/paddlePlans';
import { PaddleCheckoutButton } from '@/features/billing/ui/pricing/PaddleCheckoutButton';
import { PaddleCheckoutProvider } from '@/features/billing/model/PaddleCheckoutProvider';
import { Link } from '@/i18n/navigation';
import { PaddlePendingUsageCheckout } from '@/features/billing/ui/PaddlePendingUsageCheckout';
import { isPaddleEnabled } from '@/entities/billing/lib/paddleFeature';
import { PaddleEnterpriseCard } from '@/features/billing/ui/pricing/PaddleEnterpriseCard';

const PricingGlobal = ({
  showPendingUsageCheckout = false,
}: {
  showPendingUsageCheckout?: boolean;
}) => {
  const paddleEnabled = isPaddleEnabled();
  const plans = getPaddlePlans();

  return (
    <PaddleCheckoutProvider>
      <main className="main:pt-[186px] flex w-full flex-col items-center px-[20px] pt-[120px] pb-[80px]">
        {paddleEnabled && showPendingUsageCheckout && (
          <PaddlePendingUsageCheckout />
        )}
        <p className="text-body1 mb-[12px] font-[700] text-blue-600">
          Winging Global
        </p>
        <h1 className="text-heading2 text-center font-[800]">
          Choose the plan that fits your team
        </h1>
        <p className="text-body1 mt-[12px] mb-[48px] text-center text-gray-500">
          {paddleEnabled
            ? 'Secure monthly billing powered by Paddle. Cancel anytime.'
            : 'Live checkout will open after account approval.'}
        </p>
        <section className="main:grid-cols-3 grid w-full max-w-[1080px] grid-cols-1 gap-[20px]">
          {plans.map((plan) => (
            <article
              key={plan.name}
              className="flex min-h-[360px] flex-col rounded-[20px] border border-gray-200 bg-white p-[28px] shadow-sm"
            >
              <h2 className="text-title1 font-[800]">{plan.name}</h2>
              <div className="mt-[18px] mb-[24px] flex items-end gap-[4px]">
                <span className="text-heading1 font-[800]">
                  ${plan.monthlyPrice}
                </span>
                <span className="text-body1 pb-[6px] text-gray-500">
                  / month
                </span>
              </div>
              <p className="text-body2 mb-[18px] font-[600] text-blue-600">
                ${getPaddleFirstMonthPrice(plan.monthlyPrice)} for your first
                month as a new account
              </p>
              <p className="text-caption1 mb-[18px] text-gray-500">
                Renews monthly at ${plan.monthlyPrice} until canceled.
              </p>
              <p className="text-body1 mb-[32px] flex-1 leading-[24px] text-gray-500">
                {plan.description}
              </p>
              {paddleEnabled ? (
                <PaddleCheckoutButton plan={plan} />
              ) : (
                <Link
                  href="/auth/sign-in"
                  className="text-body1 flex h-[48px] w-full items-center justify-center rounded-[12px] bg-blue-600 font-[600] text-white hover:bg-blue-700"
                >
                  Create an account
                </Link>
              )}
            </article>
          ))}
          <PaddleEnterpriseCard />
        </section>
        <section className="mt-[32px] w-full max-w-[1080px] rounded-[20px] border border-gray-200 bg-white p-[28px]">
          <h2 className="text-title1 font-[800]">Voice conversation add-on</h2>
          <p className="text-heading2 mt-[12px] font-[800]">
            $39 / agent / month
          </p>
          <p className="text-body1 mt-[12px] text-gray-500">
            Includes 300 transcription minutes and 500,000 synthesized
            characters per agent each month.
          </p>
          <dl className="main:grid-cols-2 mt-[24px] grid grid-cols-1 gap-[16px]">
            <div className="rounded-[12px] bg-gray-50 p-[18px]">
              <dt className="text-body2 font-[700]">Transcription overage</dt>
              <dd className="text-title2 mt-[6px] font-[800]">
                $0.08 / minute
              </dd>
            </div>
            <div className="rounded-[12px] bg-gray-50 p-[18px]">
              <dt className="text-body2 font-[700]">Synthesis overage</dt>
              <dd className="text-title2 mt-[6px] font-[800]">
                $0.05 / 1,000 characters
              </dd>
            </div>
          </dl>
          <p className="text-caption1 mt-[18px] text-gray-500">
            Optional usage-based charges are disclosed before use and added to
            the next Paddle invoice. Taxes are calculated at checkout.
          </p>
        </section>
        <p className="text-caption1 mt-[28px] text-center text-gray-500">
          {`New Starter and Pro accounts receive $${PADDLE_SIGNUP_DISCOUNT} off their first month.`}
          <br />
          Taxes are calculated at checkout. Payments and invoices are handled by
          Paddle.
          <br />
          By subscribing, you agree to our{' '}
          <Link className="underline" href="/terms" target="_blank">
            Terms
          </Link>{' '}
          and{' '}
          <Link className="underline" href="/refund" target="_blank">
            Refund and Cancellation Policy
          </Link>
          .
        </p>
      </main>
    </PaddleCheckoutProvider>
  );
};

export default PricingGlobal;
